import { useMemo } from 'react';
import { Offer } from '../../services/exports/Interfaces';
import { BasketLineItem } from '../../store/types';
import useBasket from '../../hooks/basket/useBasket';
import usePreviewableOffers from '../../features/offers/hooks/usePreviewableOffers';

export interface MenuItemPrice {
  price: number;
  originalPrice: number;
}

/**
 * Prices the product modal's line item — the discounted total for the CTA, plus the undiscounted
 * total to strike through.
 *
 * Offers come from `usePreviewableOffers` rather than the basket's applied offers, so a product
 * discount shows before the item has been added. Previously the modal only saw offers that were
 * already applied, which for the by-products kinds required the item to be in the basket already.
 *
 * `originalPrice` is read off the same compiled item as `price`, so it uses the canonical
 * `(base_price + sum(modifier.original_price * modifier.quantity)) * quantity` arithmetic and
 * matches what the basket row shows. Computing it separately used to drop modifier quantities and
 * nested modifiers.
 *
 * @param extraOffer An offer the modal was opened for (offer / free-items modal), which may not be
 *   previewable on its own — e.g. a free-item reward the customer is picking.
 */
export default function useMenuItemPrice(lineItem: BasketLineItem, extraOffer?: Offer): MenuItemPrice {
  const { compilePreviewItem, originalItems } = useBasket();
  const { offers } = usePreviewableOffers();

  const previewOffers = useMemo(
    () => (extraOffer && !offers.some((offer) => offer.id === extraOffer.id) ? [...offers, extraOffer] : offers),
    [offers, extraOffer?.id],
  );

  const item = useMemo(
    () => compilePreviewItem(lineItem, previewOffers),
    // `compilePreviewItem` is intentionally not a dependency — the basket context object is a fresh
    // literal on every render and would defeat this memo.
    [lineItem, previewOffers, originalItems],
  );

  return {
    price: item?.actual_price,
    originalPrice: item?.original_price,
  };
}
